// Theme parity with Writers Room (the UI guiding principle): seed from
// localStorage → OS preference → light, persist the choice, and apply the `dark`
// class on <html> before first paint. SE previously toggled `dark` per-page with
// no persistence/seed, so it always reset to light while WR stayed dark — the
// single biggest reason the two apps looked different.
export type Theme = 'light' | 'dark';

const KEY = 'wr_ui_theme'; // same key/convention as Writers Room

function initialTheme(): Theme {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved === 'light' || saved === 'dark') return saved;
    if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) return 'dark';
  } catch {
    /* SSR/no-storage — fall through */
  }
  return 'light';
}

let current: Theme = initialTheme();
const listeners = new Set<(t: Theme) => void>();

function apply(t: Theme): void {
  document.documentElement.classList.toggle('dark', t === 'dark');
}

// Apply at module load, before React renders — no flash of the wrong theme.
apply(current);

export function getTheme(): Theme {
  return current;
}

export function setTheme(t: Theme): void {
  current = t;
  try {
    localStorage.setItem(KEY, t);
  } catch {
    /* ignore */
  }
  apply(t);
  listeners.forEach((l) => l(t));
}

export function toggleTheme(): void {
  setTheme(current === 'dark' ? 'light' : 'dark');
}

// React hook so header toggles re-render on change.
import { useEffect, useState } from 'react';
export function useTheme(): Theme {
  const [t, setT] = useState<Theme>(current);
  useEffect(() => {
    const l = (x: Theme) => setT(x);
    listeners.add(l);
    setT(current);
    return () => {
      listeners.delete(l);
    };
  }, []);
  return t;
}
